import React, { useState, useEffect, useCallback } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import colors from '@/assets/scss/_themes-vars.module.scss'
import ReactMarkdown from 'react-markdown'
import { isHtmlContent } from '@/utils/helpers'

// Initialize dayjs plugins
dayjs.extend(relativeTime)

const cleanContentForCopy = (content: string): string => {
  let cleaned = content

  cleaned = cleaned.replace(/\s*\[tool_interaction\]\([^)]+\)\s*/g, '')

  cleaned = cleaned.replace(/```[\w]*\n?[\s\S]*?```/g, '')

  cleaned = cleaned.replace(/\n{3,}/g, '\n\n')

  cleaned = cleaned
    .split('\n')
    .map((line) => line.trim())
    .join('\n')

  cleaned = cleaned.trim()

  return cleaned
}

export interface FileAttachment {
  filename: string
  url: string
  key: string
  objectType?: string
}

export interface ChatMessageData {
  text: string
  attachedFiles?: FileAttachment[]
}

type ChatMember = {
  id: string
  name: string
  surname: string
  // eslint-disable-next-line no-unused-vars
  renderIcon: (props: { sx: React.CSSProperties }) => React.ReactElement
}

export interface ChatAuthor {
  id: string
  name: string
  surname: string
  renderIcon: (props: { sx: React.CSSProperties }) => React.ReactElement
}

export interface ChatBoxProps {
  /**
   * Message content (text, attachments, etc)
   */
  data: ChatMessageData
  /**
   * Message timestamp
   */
  date: string
  /**
   * Message author information
   */
  author?: ChatAuthor
  /**
   * Message type
   */
  type?: 'text'
  /**
   * Whether the message is from the current user
   */
  isMine: boolean
  /**
   * Users who have read the message
   */
  reads: ChatMember[]
  /**
   * Optional custom content renderer
   */
  renderContent?: () => React.ReactNode
  /**
   * Whether to show the copy button
   */
  showCopyButton?: boolean
  /**
   * Content to copy when copy button is clicked (defaults to data.text)
   */
  copyContent?: string
  /**
   * Optional custom copy handler
   */
  onCopy?: (content: string) => void | Promise<void>
}

// Icon styles
const iconStyle = {
  fontSize: '25px',
  padding: '4px',
  color: colors.gray500,
}

export function ChatBox(props: ChatBoxProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { data, date, author, type = 'text', isMine, reads, renderContent, showCopyButton = false, copyContent, onCopy } = props
  const icon = author?.renderIcon ? author.renderIcon({ sx: iconStyle }) : null
  const authorName = `${author?.name || ''} ${author?.surname || ''}`.trim()
  const [formattedDate, setFormattedDate] = useState('')
  const [copied, setCopied] = useState(false)
  const isHtml = isHtmlContent(data?.text?.trim())

  useEffect(() => {
    const updateDate = () => {
      const newFormattedDate = dayjs(date).isSame(dayjs(), 'day') ? dayjs(date).fromNow() : dayjs(date).format('MMM D, HH:mm')
      setFormattedDate(newFormattedDate)
    }

    updateDate() // Initial update
    const intervalId = setInterval(updateDate, 60000) // Update every minute

    return () => clearInterval(intervalId) // Cleanup on unmount
  }, [date])

  const handleCopy = useCallback(async () => {
    try {
      const rawContent = copyContent || data?.text || ''
      const contentToCopy = cleanContentForCopy(rawContent)
      if (onCopy) {
        await onCopy(contentToCopy)
      } else {
        await navigator.clipboard.writeText(contentToCopy)
      }
      setCopied(true)
      enqueueSnackbar('Message copied to clipboard', { variant: 'success', autoHideDuration: 2000 })
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy message:', error)
      enqueueSnackbar('Error copying message', { variant: 'error' })
    }
  }, [copyContent, data?.text, onCopy, enqueueSnackbar, cleanContentForCopy])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: isMine ? 'flex-end' : 'flex-start',
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMine ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          maxWidth: isMine ? '85%' : '100%',
        }}
      >
        {/* Avatar/Icon */}
        {icon ? (
          <Box sx={{ mx: 1 }}>{icon}</Box>
        ) : (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: isMine ? 'primary.main' : 'secondary.main',
              mx: 1,
            }}
          >
            {authorName.charAt(0).toUpperCase()}
          </Avatar>
        )}

        {/* Message Content */}
        <Box
          sx={{
            backgroundColor: isMine ? '#e3f2fd' : '#f5f5f5',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            maxWidth: 'calc(100% - 50px)',
            mb: 0.5,
            boxShadow: 1,
            position: 'relative',
            '&:hover .copy-button': {
              opacity: 1,
            },
          }}
        >
          {/* Author name */}
          <Typography variant="subtitle2" color="text.secondary" component="div" gutterBottom>
            {authorName}
          </Typography>

          {/* Message content - either custom renderer or text */}
          {renderContent ? (
            renderContent()
          ) : (
            <Typography
              variant="body1"
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
              }}
            >
              {isHtml ? <div dangerouslySetInnerHTML={{ __html: data?.text }} /> : <ReactMarkdown>{data?.text}</ReactMarkdown>}
            </Typography>
          )}

          {/* File attachments */}
          {data?.attachedFiles && data.attachedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Files')}
              </Typography>
              <Stack spacing={1}>
                {data.attachedFiles.map((file, index) => (
                  <Button
                    key={`${file.key}-${index}`}
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    component="a"
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textTransform: 'none',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {file.filename}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {/* Timestamp */}
          <Typography variant="caption" color="text.secondary" component="div" align="right" sx={{ mt: 1 }}>
            {formattedDate}
          </Typography>

          {/* Copy Button */}
          {showCopyButton && (
            <Tooltip title={copied ? 'Copied!' : 'Copy message'}>
              <IconButton
                size="small"
                onClick={handleCopy}
                className="copy-button"
                sx={{
                  position: 'absolute',
                  right: 5,
                  bottom: -30,
                  transition: 'opacity 0.2s',
                }}
                aria-label="Copy message"
              >
                {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ChatBox
